import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { DEV_MODE, ENCRYPT_PHARAGRAPH } from "../config/const";
import { IGeneralWebsocketResponse } from "../store/models/FingerPrintModel";
import { OptionsObject, SnackbarKey, SnackbarMessage } from "notistack";
import { useDispatch } from "react-redux";
import { setLoadingFingerPrint } from "../store/slices/FingerPrintSlice";
import { getUrlWebSocketByRole } from "../utils/general/utils";
import { useCallback, useRef } from "react";

const initialResponseSocket: IGeneralWebsocketResponse = {
  dateTime: "",
  httpStatus: 0,
  message: "",
  responsePrintFinger: {
    deviceSerial: "",
    ip: "",
    mac: "",
    prinfingerTemplate: "",
  },
  responseCode: "",
};

const useFingerWebSocket = (fingerDevice?: any) => {
  // dependencies
  var aesEcb = require("aes-ecb");
  // Constants
  var urlSocket = getUrlWebSocketByRole();
  const needToEncryptData = sessionStorage.getItem("aes-encrypt");
  // REDUX
  const dispatch = useDispatch();

  /**
   * Refs: cada instancia del hook tiene su propio cliente STOMP y estado de respuesta.
   * Antes eran variables de módulo (fuera del hook), compartidas entre TODAS las
   * instancias. Eso causaba que al capturar la segunda huella, el stompClient anterior
   * fuera sobreescrito y las suscripciones del socket viejo siguieran activas,
   * mezclando respuestas de capturas distintas y generando mensajes fuera de orden.
   */
  const stompClientRef = useRef<any>(null);
  const responseConnectRef = useRef<string>("");
  const responseSocketTempRef = useRef<IGeneralWebsocketResponse>(initialResponseSocket);

  /**
   * Encrypt data
   * @function
   * @param {any} data data to submit
   */
  const encryptDataToSend = (data: any) => {
    const dataParsed = JSON.stringify(data);
    let encryptedData = aesEcb.encrypt(ENCRYPT_PHARAGRAPH, dataParsed || "");
    return encryptedData;
  };
  /**
   * Decrypt data
   * @function
   * @param {any} data data to submit
   */
  const decryptDataReceibed = (data: any) => {
    if (!data) return null;
    try {
      const dataDecrypted = aesEcb.decrypt(ENCRYPT_PHARAGRAPH, data);
      return JSON.parse(dataDecrypted);
    } catch (error) {
      return null;
    }
  };

  const setResponseConnect = (re: any) => {
    if (re) {
      responseConnectRef.current = re;
    }
  };

  const getReturnResponseConnect = () => {
    return responseConnectRef.current;
  };

  const returnResponseSend = () => {
    return responseSocketTempRef.current;
  };

  function isEmptyObject(obj: any) {
    try {
      const objParsed = JSON.parse(obj);
      return objParsed;
    } catch (error) { }
  }

  // open new connection to desktop web socket and try to connect in local if fail at first time
  const openSocketConnection = (
    urlSocket: string,
    onResponse: any,
    enqueueSnackbar: (
      message: SnackbarMessage,
      options?: OptionsObject
    ) => SnackbarKey,
    _customFunction: any
  ) => {
    let socket = new SockJS(urlSocket + "printfinger", null, {});
    stompClientRef.current = Stomp.over(socket);
    stompClientRef.current.debug = null;
    stompClientRef.current.connect(
      {},
      (frame: any) => {
        setResponseConnect(frame.command);
        /**
         * Secuencia correcta: suscribirse al topic PRIMERO, luego notificar
         * que la conexión está lista (onResponse).
         * Esto garantiza que cuando onResponse dispare el envío del mensaje
         * al desktop, el listener en /topic/printfingerData ya está registrado
         * y no se perderá la respuesta del dispositivo.
         */
        suscribe(_customFunction);
        onResponse(true);
      },
      (error: any) => {
        setResponseConnect(error);
        enqueueSnackbar("Error de comunicación con biometria desktop.", {
          variant: "error",
        });
        onResponse(false);
        dispatch(setLoadingFingerPrint(false));
      }
    );
  };

  /* Connect to topic using web sockets */
  const connectToFingerWebSocket = (
    onResponse: any,
    _customFunction: any,
    enqueueSnackbar: (
      message: SnackbarMessage,
      options?: OptionsObject
    ) => SnackbarKey
  ) => {
    if (urlSocket?.role === "movil") {
      let socket = new WebSocket(`${urlSocket.url}printfinger`);

      socket.onopen = () => {
        const deviceinfo = fingerDevice.result;
        socket.send(
          JSON.stringify({
            device: `${deviceinfo.device}`,
            encrypt: `${deviceinfo.encrypt}`,
            image: false,
            companyId: `${deviceinfo.companyId}`,
            deviceId: `${deviceinfo.deviceId}`,
            deviceSerial: `${deviceinfo.deviceSerial || deviceinfo.serial}`,
            dev: DEV_MODE,
          })
        );
      };

      socket.onmessage = (_response) => {
        if (isEmptyObject(_response.data)) {
          setResponseConnect("CONNECTED");
          onResponse(true);
          suscribe(_customFunction, _response.data);
          socket.close();
        }
      };
    } else {
      // Reutilizar la misma url por la que efectivamente respondió el clientInfo
      // (finger.gse, 127.0.0.1:8080 o el checkConfig 127.0.0.1:8087), en vez de
      // adivinar entre urlLocal/url según el flag aes-encrypt: ese flag indica si
      // hay que cifrar, no cuál de las tres urls quedó activa.
      const connectedSocketUrl = sessionStorage.getItem("socket-connected-url");

      openSocketConnection(
        connectedSocketUrl ||
        (needToEncryptData === "1" ? urlSocket?.urlLocal : urlSocket?.url),
        onResponse,
        enqueueSnackbar,
        _customFunction
      );
    }
  };

  /* Disconnect */
  function disconnect() {
    if (!!stompClientRef.current) {
      stompClientRef.current.disconnect();
      // Limpiar la referencia para que la próxima captura empiece desde cero
      // y no herede suscripciones activas del socket anterior
      stompClientRef.current = null;
    }
  }

  const suscribe = (_customFunction: any, data?: any) => {
    if (!!stompClientRef.current) {
      stompClientRef.current.subscribe(
        "/topic/printfingerData",
        (message: any) => {
          callbackSuscribe(message, _customFunction, needToEncryptData === "1");
        }
      );
    } else {
      callbackSuscribe({ body: data }, _customFunction);
    }
  };

  const removeFingerImage = (printFinger: any) => {
    if (!printFinger) return printFinger;
    const { imageBase64, ...printFingerData } = printFinger;
    return printFingerData;
  };

  const callbackSuscribe = (
    message: any,
    _customFunction: any,
    isEncrypted?: Boolean
  ) => {
    if (message) {
      let messageParsed = JSON.parse(message.body);

      let responsePrintFingerData = isEncrypted
        ? decryptDataReceibed(messageParsed.responsePrintFingerEncrypted)
        : messageParsed.responsePrintFinger;

      responseSocketTempRef.current = {
        dateTime: messageParsed.dateTime,
        httpStatus: messageParsed.httpStatus,
        message: messageParsed.message,
        responsePrintFinger: removeFingerImage(responsePrintFingerData),
        responseCode: messageParsed.responseCode,
      };

      _customFunction(responseSocketTempRef.current);
    }
  };

  /* Send place */
  const sendSocket = useCallback(
    (deviceInfo: any) => {
      if (!!stompClientRef.current) {
        let objtoSend = {
          device: `${deviceInfo.device}`,
          encrypt: `${deviceInfo.encrypt}`,
          image: false,
          companyId: `${deviceInfo.companyId}`,
          deviceId: `${deviceInfo.deviceId}`,
          deviceSerial: `${deviceInfo.deviceSerial || deviceInfo.serial}`,
          dev: DEV_MODE,
        };

        stompClientRef.current.send(
          "/app/printfinger",
          {},
          needToEncryptData === "1"
            ? encryptDataToSend(objtoSend)
            : JSON.stringify(objtoSend)
        );
      }
    },
    [needToEncryptData]
  );

  function sendRequestSocket(deviceInfo: any) {
    sendSocket(deviceInfo);
  }

  return {
    sendRequestSocket,
    disconnect,
    connectToFingerWebSocket,
    returnResponseConnect: getReturnResponseConnect,
    returnResponseSend,
  };
};

export default useFingerWebSocket;
